import { useTranslation } from "react-i18next";
import useFormHook from "@shared/hooks/useFormHook";
import { excessByEmployerService } from "../services/excessByEmployer";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useForm } from "react-hook-form";
import { useState, useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import LoadingButton from '@shared/components/LoadingButton';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';

export default function ExcessByEmployerForm({ onClose, onConfirm, editData, formProps }) {
    const { t } = useTranslation();
    const validationSchema = yup.object({
        code: yup.string().required(t('common.required')),
        reason: yup.string().required(t('common.required')),
        kindOfCompensation: yup.string().required(t('common.required')),
    });
    const { modalRef } = useFormHook({}, onClose);
    const { register, handleSubmit, setValue, reset, formState: { errors } } =
        useForm({
            resolver: yupResolver(validationSchema),
            defaultValues: {
                active: true
            },
            ...formProps
        });
    const [loading, setLoading] = useState(false);
    const [fetchingData, setFetchingData] = useState(false);

    // Fetch data if editing
    useEffect(() => {
        const fetchExcessByEmployerData = async () => {
            if (editData && editData.id) {
                try {
                    setFetchingData(true);
                    const data = await excessByEmployerService.getExcessByEmployerById(editData.id);
                    
                    setValue('code', data.code || '');
                    setValue('reason', data.reason || '');
                    setValue('kindOfCompensation', data.kindOfCompensation || '');
                    setValue('active', data.active !== undefined ? data.active : true);
                } catch (error) {
                    console.error('Error fetching excess by employer data:', error);
                } finally {
                    setFetchingData(false);
                }
            } else {
                reset({
                    active: true
                });
            }
        };

        fetchExcessByEmployerData();
    }, [editData, setValue, reset]);

    const submitForm = async (data) => {
        try {
            setLoading(true);
            const formData = {
                code: data.code,
                reason: data.reason,
                kindOfCompensation: data.kindOfCompensation,
                active: data.active
            };
            if (onConfirm) {
                await onConfirm(formData);
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data || t('common.errorMessage');
            notifyError(errorMessage);
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {editData ? t('common.edit') : t('common.add')} 
                        </h4>
                    </div>
                    {fetchingData ? (
                        <div className="modal-body">
                            <p>{t('common.loading') || 'Loading...'}</p>
                        </div>
                    ) : (
                        <form id="excessByEmployerForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections" style={{ display: 'flex', justifyContent: 'center' }}>
                                <div className="form-section" style={{ maxWidth: '500px', width: '100%' }}>
                                    <div className="form-group">
                                        <span>{t('staff.excessByEmployer.table.headers.code')}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={10}
                                            className={`form-control ${errors.code ? 'input-error' : ''}`}
                                            placeholder={errors.code ? errors.code.message : t('common.required')}
                                            {...register('code')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.excessByEmployer.table.headers.reason')}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={100}
                                            className={`form-control ${errors.reason ? 'input-error' : ''}`}
                                            placeholder={errors.reason ? errors.reason.message : t('common.required')}
                                            {...register('reason')} />
                                    </div>
                                    <div className="form-group">
                                        <span>{t('staff.excessByEmployer.table.headers.kindOfCompensation')}<span className="required">*</span></span>
                                        <input type="text"
                                            maxLength={100}
                                            className={`form-control ${errors.kindOfCompensation ? 'input-error' : ''}`}
                                            placeholder={errors.kindOfCompensation ? errors.kindOfCompensation.message : t('common.required')}
                                            {...register('kindOfCompensation')} />
                                    </div>
                                    <div className="form-group">
                                        <div className="checkbox-wrapper-start title-checkbox">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                {...register('active')}
                                            />
                                            <label htmlFor="active">{t('staff.excessByEmployer.table.headers.active')}</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </form>
                    )}
                    <div className="modal-footer">
                        <LoadingButton type="submit" form="excessByEmployerForm" className="btn btn-primary" loading={loading} disabled={fetchingData}>
                            {t('common.save')}
                        </LoadingButton>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

